import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Editing a vendor's details (PRD §F1).
 *
 * Same fields as `AddVendorDialog`, with one deliberate omission: **email
 * addresses are not here**. They are managed on the detail page by their own
 * add/remove controls, because an address is what inbound matching keys on —
 * dropping one by omitting it from a form payload would silently stop filing a
 * vendor's replies. The API enforces the same split (`emails` is rejected on
 * update); this dialog just doesn't pretend otherwise.
 */

export interface EditableVendor {
  id: string;
  businessName: string;
  contactName: string | null;
  phone: string | null;
  website: string | null;
  primaryCategory: string | null;
  notes: string | null;
}

const NO_CATEGORY = '__none__';

export default function EditVendorDialog({
  vendor,
  open,
  onOpenChange,
}: {
  vendor: EditableVendor;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await client.api.categories.$get();
      if (!res.ok) throw new Error('Failed to load categories');
      return res.json();
    },
    enabled: open,
  });

  const blank = () => ({
    businessName: vendor.businessName,
    contactName: vendor.contactName ?? '',
    phone: vendor.phone ?? '',
    website: vendor.website ?? '',
    primaryCategory: vendor.primaryCategory ?? '',
    notes: vendor.notes ?? '',
  });

  const [form, setForm] = useState(blank);

  // Reopening must show what is stored, not last time's abandoned edits.
  useEffect(() => {
    if (open) setForm(blank());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendor]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await client.api.vendors[':id'].$put({
        param: { id: vendor.id },
        json: {
          businessName: form.businessName.trim(),
          contactName: form.contactName.trim() || null,
          phone: form.phone.trim() || null,
          website: form.website.trim() || null,
          primaryCategory: form.primaryCategory || null,
          notes: form.notes.trim() || null,
        },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to save this vendor');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendor.id] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      onOpenChange(false);
      toast.success('Vendor updated');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit vendor</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="v-businessName">Business name *</Label>
            <Input
              id="v-businessName"
              autoFocus
              value={form.businessName}
              onChange={(e) => setForm({ ...form, businessName: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="v-contactName">Contact name</Label>
              <Input
                id="v-contactName"
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="v-phone">Phone</Label>
              <Input
                id="v-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="v-category">Category</Label>
              <Select
                value={form.primaryCategory || NO_CATEGORY}
                onValueChange={(v) =>
                  setForm({ ...form, primaryCategory: v === NO_CATEGORY ? '' : v })
                }
              >
                <SelectTrigger id="v-category">
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                  {(categories ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="v-website">Website</Label>
              <Input
                id="v-website"
                placeholder="bloomfloral.com"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="v-notes">Notes</Label>
            <Textarea
              id="v-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Email addresses are managed below — replies from any of them are matched to this vendor,
            so they're never changed by saving this form.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={save.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!form.businessName.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
